import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import React from 'react';

import Panel from 'react-bootstrap/lib/Panel';

import {
  faRocket,
  faRuler,
  faList,
  faChartLine,
  faDollarSign,
} from '@fortawesome/free-solid-svg-icons';

import Link from '../../../../../components/Link';
import PageLayout from '../../../../../components/PageLayout';

import ShadowerLink from '../../../../../components/ShadowerLink';

import Navbar from '../../../../../components/Handbook/Navbar';

import s from '../../HandbookPage.scss';

class HowThePlatformWorksPage extends React.Component {
  render() {
    return (
      <PageLayout className={s.page}>
        <div className={s.container}>
          <div className={s.navbar}>
            <div className={s.title}>Shadowing Handbook</div>
            <Navbar
              navbarIcon={faRocket}
              navbarSection="Getting Started"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/welcome-to-shadower"
                  >
                    Welcome to Shadower
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/how-shadower-works"
                  >
                    How Shadower Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/working-with-businesses"
                  >
                    Working with Businesses
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/shadowing-guidelines"
                  >
                    Shadowing Guidelines
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faRuler}
              navbarSection="Planning a Session"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/choosing-a-topic"
                  >
                    Choosing a Topic
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/interactivity-level"
                  >
                    Interactivity Level
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/session-type"
                  >
                    Session Type
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/providing-resources"
                  >
                    Providing Resources
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faList}
              navbarSection="Managing a Session"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/expectations"
                  >
                    Expectations
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/session-flow"
                  >
                    Session Flow
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/maximizing-your-time"
                  >
                    Maximizing Your Time
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faChartLine}
              navbarSection="Promoting Your Profile"
              navbarSubsection={
                <div>
                  <Link
                    className={cx(s.link, s.active)}
                    to="/handbook/promoting-your-profile/how-the-platform-works"
                  >
                    How the Platform Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/optimizing-your-profile"
                  >
                    Optimizing Your Profile
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/marketing-yourself"
                  >
                    Marketing Yourself
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/how-to-build-a-following"
                  >
                    How to Build a Following
                  </Link>
                </div>
              }
              overrideDefault
            />

            <Navbar
              navbarIcon={faDollarSign}
              navbarSection="Earning Revenue"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/earning-revenue/how-payment-works"
                  >
                    How Payment Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/earning-revenue/connecting-with-the-community"
                  >
                    Connecting with the Community
                  </Link>
                </div>
              }
            />

            <Link className={s.newProfileLink} to="/user/profiles/new">
              Create a shadowing profile
            </Link>
          </div>

          <div className={s.explanation}>
            <Panel>
              <Panel.Heading>
                <div>Promoting Your Profile</div>
              </Panel.Heading>
              <Panel.Body>
                <div className={s.handbookTitle}>A guide for experts</div>
                <div className={s.subsectionTitle}>How the Platform Works</div>

                <img
                  alt="Shadower home page"
                  src="/assets/images/handbook/platform@1280x600.jpg"
                />

                <p>
                  Understanding how Shadower ranks experts and their profiles is
                  essential to getting discovered and succeeding on the
                  platform.
                </p>

                <div className={s.contentContainer}>
                  <div>
                    <strong>Ratings</strong>
                  </div>
                  <p>
                    Shadower relies on a rating system to provide users with new
                    potential shadowing opportunities. It is imperative that you
                    maintain a high rating for a number of reasons:
                  </p>

                  <ul>
                    <li>
                      Trust and safety:
                      <p>
                        Users are much more likely to shadow you if you have a
                        higher rating with multiple positive reviews.
                      </p>
                    </li>

                    <li>
                      Qualified expert list:
                      <p>
                        When users first visit the homepage, they will see the
                        qualified experts near their location.
                      </p>
                    </li>

                    <li>
                      Maintaining good standing:
                      <p>
                        If you receive too many negative ratings and reviews,
                        you will receive an alert and, if desired, assistance to
                        bring your profile or session up to standards. If the
                        negative ratings continue, your profile may be suspended
                        or removed entirely.
                      </p>
                    </li>
                  </ul>
                </div>

                <div className={s.contentContainer}>
                  <div>
                    <strong>Trending Shadowing Profiles</strong>
                  </div>

                  <p>
                    Besides showing site visitors the highest-rated experts
                    nearby, Shadower also uses an algorithm to rank the most
                    popular shadowing profiles nearby. This algorithm is based
                    on:
                  </p>

                  <ul>
                    <li>
                      Your ratings:
                      <p>
                        We look at your overall ratings, as well as your recent
                        ratings.
                      </p>
                    </li>

                    <li>
                      Your number of bookings:
                      <p>
                        We look at your overall number of bookings, as well as
                        your recent number of bookings.
                      </p>
                    </li>

                    <li>
                      Your number of profile favorites:
                      <p>
                        We look at how many people have favorited your profile
                        overall, as well as how many have favorited it recently.
                      </p>
                    </li>

                    <li>
                      Your profile views:
                      <p>
                        We look at how many people have looked at your profile
                        recently.
                      </p>
                    </li>
                  </ul>
                </div>

                <ShadowerLink
                  className={s.nextLink}
                  to="/handbook/promoting-your-profile/optimizing-your-profile"
                >
                  Next {'>'}
                </ShadowerLink>
              </Panel.Body>
            </Panel>
          </div>
        </div>
      </PageLayout>
    );
  }
}

export default withStyles(s)(HowThePlatformWorksPage);
